import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import { ApiResponse } from '../../../types/api';
import { Config } from '../../../services/config';
import ViewLayout from '../../../components/Common/ViewLayout';

interface ChecklistDetail {
  id: number;
  entityType: 'veiculo' | 'equipamento';
  veiculo: string;
  equipamento: string;
  motorista: string;
  data_checklist: string;
  tipo: string;
  status: string;
  resultado: string;
  observacoes: string;
  quilometragem: number | null; // Permitir quilometragem null/nullish
  itens_verificados: number;
  itens_aprovados: number;
  itens_reprovados: number;
  itens: ChecklistItem[];
  criado_em: string;
  atualizado_em: string;
}

interface ChecklistItem {
  id: number;
  item: string;
  status: string;
  observacao: string;
  categoria: string;
  obrigatorio: boolean;
  fotos: string[];
}

const ChecklistView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState<ChecklistDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadChecklist = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiService.getChecklist(Number(id)) as ApiResponse<any>;
      
      if (response.success && response.data) {
        // Converter dados da API para o formato do frontend
        const apiChecklist = response.data;

        // Detectar o tipo de checklist (veículo ou equipamento)
        const isEquipmentChecklist = apiChecklist.equipamento_id != null;
        const entityType = isEquipmentChecklist ? 'equipamento' : 'veiculo';

        const convertedChecklist: ChecklistDetail = {
          id: apiChecklist.id,
          entityType: entityType,
          veiculo: apiChecklist.veiculo_placa || '',
          equipamento: apiChecklist.equipamento_tag && apiChecklist.equipamento_nome
            ? `${apiChecklist.equipamento_tag} - ${apiChecklist.equipamento_nome}`
            : apiChecklist.equipamento_tag || apiChecklist.equipamento_nome || '',
          motorista: apiChecklist.motorista_nome,
          data_checklist: apiChecklist.data_realizacao,
          tipo: apiChecklist.tipo === 'pre_viagem' ? 'Pré-viagem' :
                apiChecklist.tipo === 'pos_viagem' ? 'Pós-viagem' :
                apiChecklist.tipo === 'diario' ? 'Diário' : 'Periódico',
          status: apiChecklist.status === 'concluido' ? 'Aprovado' :
                  apiChecklist.status === 'reprovado' ? 'Reprovado' : 'Pendente',
          resultado: apiChecklist.itens.length > 0
            ? `${Math.round((apiChecklist.itens.filter((item: any) => item.valor === 'aprovado' || item.valor === 'conforme').length / apiChecklist.itens.length) * 100)}%`
            : '0%',
          observacoes: apiChecklist.observacoes || '',
          quilometragem: apiChecklist.odometro,
          itens_verificados: apiChecklist.itens.length,
          itens_aprovados: apiChecklist.itens.filter((item: any) => item.valor === 'aprovado' || item.valor === 'conforme').length,
          itens_reprovados: apiChecklist.itens.filter((item: any) => item.valor === 'reprovado' || item.valor === 'nao_conforme').length,
          itens: apiChecklist.itens.map((item: any) => ({
            id: item.id,
            item: item.descricao,
            status: (item.valor === 'aprovado' || item.valor === 'conforme') ? 'Aprovado' :
                   (item.valor === 'reprovado' || item.valor === 'nao_conforme') ? 'Reprovado' : 'Pendente',
            observacao: item.observacao || '',
            categoria: item.categoria,
            obrigatorio: item.obrigatorio,
            fotos: Array.isArray(item.fotos) ? item.fotos : (item.fotos ? [item.fotos] : [])
          })),
          criado_em: apiChecklist.criado_em,
          atualizado_em: apiChecklist.atualizado_em
        };

        setChecklist(convertedChecklist);
      } else {
        throw new Error(response.message || 'Erro ao carregar checklist');
      }
    } catch (error) {
      console.error('Erro ao carregar checklist:', error);
      setError('Erro ao carregar detalhes do checklist');
      utils.showNotification('Erro ao carregar detalhes do checklist', { type: 'error' });
      // Não injetamos dados fictícios: em caso de erro o estado de erro é exibido ao usuário.
      setChecklist(null);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Aprovado': 'status-success',
      'Reprovado': 'status-danger',
      'Pendente': 'status-warning'
    };
    return statusMap[status] || 'status-secondary';
  };

  const getStatusText = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Aprovado': 'Aprovado',
      'Reprovado': 'Reprovado',
      'Pendente': 'Pendente'
    };
    return statusMap[status] || status;
  };

  const getItemStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Aprovado': 'status-success',
      'Reprovado': 'status-danger',
      'Pendente': 'status-warning'
    };
    return statusMap[status] || 'status-secondary';
  };

  const getItemStatusText = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Aprovado': 'Aprovado',
      'Reprovado': 'Reprovado',
      'Pendente': 'Pendente'
    };
    return statusMap[status] || status;
  };

  const getApprovalPercentage = (): number => {
    if (!checklist || checklist.itens_verificados === 0) return 0;
    return Math.round((checklist.itens_aprovados / checklist.itens_verificados) * 100);
  };

  const getApprovalClass = (percentage: number): string => {
    if (percentage >= 90) return 'status-success';
    if (percentage >= 70) return 'status-warning';
    return 'status-danger';
  };

  const getApprovalText = (percentage: number): string => {
    if (percentage === 100) return 'Aprovação Total';
    if (percentage >= 90) return 'Alta Taxa de Aprovação';
    if (percentage >= 70) return 'Taxa Moderada de Aprovação';
    return 'Baixa Taxa de Aprovação';
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      utils.showNotification('Excluindo checklist...', { type: 'info' });

      // Usar o endpoint correto de delete
      const response = await apiService.deleteChecklist(Number(id)) as ApiResponse<{ message: string }>;

      if (response.success) {
        utils.showNotification('Checklist excluído com sucesso!', { type: 'success' });
        navigate('/checklists');
      } else {
        throw new Error(response.message || 'Erro ao excluir checklist');
      }
    } catch (error) {
      console.error('Erro ao excluir checklist:', error);
      utils.showNotification('Erro ao excluir checklist', { type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  if (error || !checklist) {
    return (
      <ViewLayout
        title="Erro ao Carregar"
        subtitle={error || 'Checklist não encontrado'}
        isLoading={isLoading}
        onBack={() => navigate('/checklists')}
        showActions={false}
      >
        <div style={{ textAlign: 'center', padding: '50px 20px' }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '20px' }}></i>
          <h3>{error || 'Checklist não encontrado'}</h3>
          <p>O checklist solicitado não pôde ser carregado.</p>
          <button className="btn btn-primary" onClick={() => navigate('/checklists')}>
            Voltar para Lista
          </button>
        </div>
      </ViewLayout>
    );
  }

  const approvalPercentage = getApprovalPercentage();
  const approvalClass = getApprovalClass(approvalPercentage);
  const approvalText = getApprovalText(approvalPercentage);

  return (
    <ViewLayout
      title="Detalhes do Checklist"
      subtitle={`Checklist #${checklist.id}`}
      entityId={checklist.id}
      isLoading={isLoading}
      editUrl={`/checklists/editar/${checklist.id}`}
      onDelete={handleDelete}
      deleteButtonText="Excluir Checklist"
      onBack={() => navigate('/checklists')}
    >
      {/* Informações Básicas */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
          Informações Básicas
        </h3>
        
        <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          {checklist.entityType === 'veiculo' ? (
            <div className="info-item">
              <label className="form-label">Veículo</label>
              <span className="info-value">{checklist.veiculo}</span>
            </div>
          ) : (
            <div className="info-item">
              <label className="form-label">Equipamento</label>
              <span className="info-value">{checklist.equipamento}</span>
            </div>
          )}

          <div className="info-item">
            <label className="form-label">Motorista</label>
            <span className="info-value">{checklist.motorista}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Data e Hora</label>
            <span className="info-value">{formatDateTime(checklist.data_checklist)}</span>
          </div>

          <div className="info-item">
            <label className="form-label">
              {checklist.entityType === 'veiculo' ? 'Quilometragem' : 'Odômetro/Horímetro'}
            </label>
            <span className="info-value">
              {checklist.quilometragem
                ? `${checklist.quilometragem.toLocaleString('pt-BR')} ${checklist.entityType === 'veiculo' ? 'km' : ''}`
                : 'N/A'}
            </span>
          </div>

          <div className="info-item">
            <label className="form-label">Tipo</label>
            <span className="info-value">{checklist.tipo}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Status</label>
            <span className="info-value">
              <span className={`status-badge ${getStatusClass(checklist.status)}`}>
                {getStatusText(checklist.status)}
              </span>
            </span>
          </div>

          <div className="info-item">
            <label className="form-label">Taxa de Aprovação</label>
            <span className="info-value">
              <span className={`status-badge ${approvalClass}`}>
                {approvalPercentage}%
              </span>
            </span>
          </div>

          <div className="info-item">
            <label className="form-label">Status da Aprovação</label>
            <span className="info-value">
              <span className={`status-badge ${approvalClass}`}>
                {approvalText}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Observações */}
      {checklist.observacoes && (
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Observações
          </h3>
          
          <div className="info-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="info-item">
              <label className="form-label">Observações Gerais</label>
              <div className="info-value" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                {checklist.observacoes}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Estatísticas do Checklist */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
          Estatísticas do Checklist
        </h3>
        
        <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          <div className="info-item">
            <label className="form-label">Total de Itens</label>
            <span className="info-value">{checklist.itens_verificados}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Itens Aprovados</label>
            <span className="info-value" style={{ color: '#04C64F' }}>
              {checklist.itens_aprovados}
            </span>
          </div>

          <div className="info-item">
            <label className="form-label">Itens Reprovados</label>
            <span className="info-value" style={{ color: '#e74c3c' }}>
              {checklist.itens_reprovados}
            </span>
          </div>

          <div className="info-item">
            <label className="form-label">Taxa de Aprovação</label>
            <span className="info-value">
              <span className={`status-badge ${approvalClass}`}>
                {approvalPercentage}%
              </span>
            </span>
          </div>

          <div className="info-item">
            <label className="form-label">Status Geral</label>
            <span className="info-value">
              <span className={`status-badge ${getStatusClass(checklist.status)}`}>
                {getStatusText(checklist.status)}
              </span>
            </span>
          </div>

          <div className="info-item">
            <label className="form-label">Tipo de Checklist</label>
            <span className="info-value">{checklist.tipo}</span>
          </div>
        </div>
      </div>

      {/* Itens do Checklist */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
          Itens Verificados ({checklist.itens.length})
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          {checklist.itens.map(item => (
            <div key={item.id} style={{ 
              padding: '16px', 
              border: '1px solid #e9ecef', 
              borderRadius: '8px',
              background: item.status === 'Reprovado' ? '#fff5f5' : item.status === 'Pendente' ? '#fff8e6' : '#f8f9fa',
              borderLeft: `4px solid ${item.status === 'Reprovado' ? '#e74c3c' : item.status === 'Pendente' ? '#f39c12' : '#04C64F'}`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <strong style={{ fontSize: '1rem' }}>{item.item}</strong>
                    {item.obrigatorio && (
                      <span className="status-badge status-danger" style={{ fontSize: '0.7rem' }}>
                        Obrigatório
                      </span>
                    )}
                  </div>
                  <div style={{ color: '#7f8c8d', fontSize: '0.8rem', marginBottom: '8px' }}>
                    Categoria: {item.categoria}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                  <span className={`status-badge ${getItemStatusClass(item.status)}`} style={{ fontSize: '0.7rem' }}>
                    {getItemStatusText(item.status)}
                  </span>
                  {item.obrigatorio && (
                    <span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>
                      Obrigatório
                    </span>
                  )}
                </div>
              </div>
              
              {item.observacao && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px',
                  background: item.status === 'Reprovado' ? '#ffe6e6' : '#f0f8ff',
                  borderRadius: '4px',
                  borderLeft: `3px solid ${item.status === 'Reprovado' ? Config.THEME.DANGER_COLOR : Config.THEME.PRIMARY_COLOR}`
                }}>
                  <div style={{ fontSize: '0.8rem', color: item.status === 'Reprovado' ? Config.THEME.DANGER_COLOR : Config.THEME.PRIMARY_COLOR }}>
                    <strong>Observação:</strong> {item.observacao}
                  </div>
                </div>
              )}

              {item.fotos && item.fotos.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#555', marginBottom: '6px' }}>
                    <i className="fas fa-camera" style={{ marginRight: '4px' }}></i>
                    Fotos ({item.fotos.length})
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {item.fotos.map((foto: string, fotoIndex: number) => (
                      <a
                        key={fotoIndex}
                        href={foto}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'block' }}
                      >
                        <img
                          src={foto}
                          alt={`Foto ${fotoIndex + 1} - ${item.item}`}
                          style={{
                            width: '120px',
                            height: '120px',
                            objectFit: 'cover',
                            borderRadius: '6px',
                            border: '2px solid #e9ecef',
                            cursor: 'pointer',
                            transition: 'border-color 0.2s',
                          }}
                          onMouseOver={(e) => (e.currentTarget.style.borderColor = Config.THEME.PRIMARY_COLOR)}
                          onMouseOut={(e) => (e.currentTarget.style.borderColor = '#e9ecef')}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Informações de Auditoria */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
          Informações de Auditoria
        </h3>
        
        <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          <div className="info-item">
            <label className="form-label">Criado em</label>
            <span className="info-value">{formatDateTime(checklist.criado_em)}</span>
          </div>

          <div className="info-item">
            <label className="form-label">Atualizado em</label>
            <span className="info-value">{formatDateTime(checklist.atualizado_em)}</span>
          </div>

          <div className="info-item">
            <label className="form-label">ID do Checklist</label>
            <span className="info-value">{checklist.id}</span>
          </div>
        </div>
      </div>
    </ViewLayout>
  );
};

export default ChecklistView;
